export type InterviewTechnicalInterruptionReasonCode =
  | 'QUESTION_DELIVERY_EXHAUSTED'
  | 'STT_INFRASTRUCTURE_EXHAUSTED'
  | 'MEDIA_ACCESS_EXHAUSTED'
  | 'ANSWER_RECONCILIATION_EXHAUSTED';

export type InterviewTechnicalInterruptionCheckpoint =
  | 'QUESTION_AUTHORITATIVE'
  | 'QUESTION_DELIVERED'
  | 'CANDIDATE_AUDIO_CAPTURED';

export type InterviewTechnicalInterruptionIntent = {
  userId: string;
  interviewId: number;
  reasonCode: InterviewTechnicalInterruptionReasonCode;
  checkpoint: InterviewTechnicalInterruptionCheckpoint;
  createdAt: string;
};

const STORAGE_PREFIX = 'interviewTechnicalInterruption:';

const storageKey = (userId: string, interviewId: number) => (
  `${STORAGE_PREFIX}${encodeURIComponent(userId)}:${interviewId}`
);

const keys = () => {
  try {
    return Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index))
      .filter((key): key is string => Boolean(key));
  } catch {
    return [];
  }
};

export const persistInterviewTechnicalInterruptionIntent = (
  intent: Omit<InterviewTechnicalInterruptionIntent, 'createdAt'>,
) => {
  const value: InterviewTechnicalInterruptionIntent = {
    ...intent,
    createdAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(storageKey(value.userId, value.interviewId), JSON.stringify(value));
  } catch {}
  return value;
};

export const clearInterviewTechnicalInterruptionIntent = (userId: string, interviewId: number) => {
  try {
    localStorage.removeItem(storageKey(userId, interviewId));
  } catch {}
};

export const readInterviewTechnicalInterruptionIntents = (userId: string) => {
  const prefix = `${STORAGE_PREFIX}${encodeURIComponent(userId)}:`;
  return keys()
    .filter((key) => key.startsWith(prefix))
    .map((key) => {
      try {
        return JSON.parse(localStorage.getItem(key) || '') as InterviewTechnicalInterruptionIntent;
      } catch {
        return null;
      }
    })
    .filter((intent): intent is InterviewTechnicalInterruptionIntent => Boolean(
      intent
      && intent.userId === userId
      && Number.isFinite(Number(intent.interviewId))
      && Number(intent.interviewId) > 0
    ));
};

export const reconcileInterviewTechnicalInterruptionIntents = async (
  userId: string,
  submit: (intent: InterviewTechnicalInterruptionIntent) => Promise<any>,
) => {
  const intents = readInterviewTechnicalInterruptionIntents(userId);
  const outcomes = [];
  for (const intent of intents) {
    try {
      const response = await submit(intent);
      const canonicalStatus = String(response?.data?.data?.status || '').toLowerCase();
      if (response?.data?.status === true && canonicalStatus === 'technical_interrupted') {
        clearInterviewTechnicalInterruptionIntent(userId, intent.interviewId);
        outcomes.push({ intent, reconciled: true });
      } else {
        outcomes.push({ intent, reconciled: false });
      }
    } catch (error: any) {
      const code = String(error?.response?.data?.error_code || '').toUpperCase();
      if (code === 'INTERVIEW_TECHNICAL_INTERRUPTED' || code === 'INTERVIEW_ALREADY_COMPLETED') {
        clearInterviewTechnicalInterruptionIntent(userId, intent.interviewId);
        outcomes.push({ intent, reconciled: true });
      } else {
        outcomes.push({ intent, reconciled: false });
      }
    }
  }
  return outcomes;
};
